// Prompt 0456 — Hook genérico de dirty state por tab.
// Consolida las dos copias gemelas de puesto-panel/hooks/useDirtyTabs.ts y
// rol-funcional-panel/hooks/useDirtyTabs.ts (solo diferian en el nombre del
// type parameter TabId). Ahora es un hook parametrico: cada consumidor pasa
// su union de tab ids como type parameter.

import { useState, useCallback } from 'react';

export interface DirtyTabsState<TabId extends string> {
  dirtyTabs: Set<TabId>;
  markDirty: (tab: TabId) => void;
  markClean: (tab: TabId) => void;
  markAllClean: () => void;
  isDirty: (tab: TabId) => boolean;
  anyDirty: () => boolean;
  dirtyTabIds: () => TabId[];
}

export function useDirtyTabs<TabId extends string>(): DirtyTabsState<TabId> {
  const [dirtyTabs, setDirtyTabs] = useState<Set<TabId>>(new Set());

  const markDirty = useCallback((tab: TabId) => {
    setDirtyTabs((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, []);

  const markClean = useCallback((tab: TabId) => {
    setDirtyTabs((prev) => {
      if (!prev.has(tab)) return prev;
      const next = new Set(prev);
      next.delete(tab);
      return next;
    });
  }, []);

  const markAllClean = useCallback(() => {
    setDirtyTabs((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const isDirty = useCallback((tab: TabId) => dirtyTabs.has(tab), [dirtyTabs]);
  const anyDirty = useCallback(() => dirtyTabs.size > 0, [dirtyTabs]);
  const dirtyTabIds = useCallback(() => Array.from(dirtyTabs), [dirtyTabs]);

  return { dirtyTabs, markDirty, markClean, markAllClean, isDirty, anyDirty, dirtyTabIds };
}
